/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ComplexProjectCard from "examples/Cards/ProjectCards/ComplexProjectCard";

// Analytics dashboard components
import StaffPerformance from "layouts/dashboards/analytics/components/SalesByCountry";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import DataTable from "examples/Tables/DataTable";

import { useState, useEffect } from "react";
import { API_ESA_OFFICE } from "utils";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { logoutUser } from "actions/AuthAction";

// Images
import eoCover from "assets/images/eo_cover.png";
import eoCover2 from "assets/images/eocover2.png";
import eoCover3 from "assets/images/eocover3.png";
import eoCover4 from "assets/images/eocover4.png";
import eo_mini1 from "assets/images/eo_mini1.png";
import eo_mini4 from "assets/images/eo_mini4.png";

// Konfigurasi axios global untuk mengirim credentials
axios.defaults.withCredentials = true;

function Analytics() {
  const [dinasId, setDinasId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    overview: {
      totalPegawai: 0,
      totalDinas: 0,
      totalAplikasi: 0,
      totalAset: 0,
    },
    kehadiran: {
      persentasePagi: 0,
      persentaseSiang: 0,
      totalHadir: 0,
      totalPegawai: 0,
      detailStatus: {
        hadir: 0,
        izin: 0,
        sakit: 0,
        dinasLuar: 0,
        tanpaBerita: 0,
      },
    },
    produktivitas: {
      totalLaporan: 0,
      totalKegiatan: 0,
      totalOutput: 0,
      dinasStats: [],
    },
    dokumen: {
      totalDisposisi: 0,
      totalSurat: 0,
      totalInstruksi: 0,
      totalAset: 0,
    },
  });
  const [smartDhStats, setSmartDhStats] = useState({
    hadir: 0,
    izin: 0,
    sakit: 0,
    cuti: 0,
    tugasBelajar: 0,
    tanpaBerita: 0,
    tidakAdaData: 0,
    total: 0,
  });
  const [apelStats, setApelStats] = useState({
    persentasePagi: 0,
    persentaseSiang: 0,
  });
  const [apelHadir, setApelHadir] = useState({ pagi: 0, siang: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [laporanStats, setLaporanStats] = useState({
    totalLaporan: 0,
    totalKegiatan: 0,
    totalOutput: 0,
  });
  const [topPerformers, setTopPerformers] = useState([]);
  const [apelRekapData, setApelRekapData] = useState([]);
  const [smartDhKinerjaData, setSmartDhKinerjaData] = useState([]);
  const [combinedTopPerformers, setCombinedTopPerformers] = useState([]);
  const [apelCounts, setApelCounts] = useState({
    labels: [
      "Hadir",
      "Izin",
      "Sakit",
      "Cuti",
      "Tugas Belajar",
      "Dinas Luar",
      "Tanpa Berita",
    ],
    pagi: [0, 0, 0, 0, 0, 0, 0],
    siang: [0, 0, 0, 0, 0, 0, 0],
  });
  const navigate = useNavigate();

  // Array gambar untuk slider - desktop dan mobile
  const coverImagesDesktop = [eoCover, eoCover2, eoCover3, eoCover4];
  const coverImagesMobile = [eo_mini1, eo_mini4];
  const coverImages = isMobile ? coverImagesMobile : coverImagesDesktop;

  // Deteksi ukuran layar untuk mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 960); // md breakpoint biasanya 960px
      // Reset index ketika switch antara mobile dan desktop
      setCurrentImageIndex(0);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-slide banner
  useEffect(() => {
    const imageCount = coverImages.length;
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % imageCount
      );
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [isMobile]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
    } else {
      logoutUser(navigate);
    }
  }, []);

  // Fungsi untuk mendapatkan tanggal hari ini dalam format WITA
  const getTodayWITA = () => {
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const utc = today.getTime() + today.getTimezoneOffset() * 60000;
    const witaTime = new Date(utc + witaOffset * 60000);
    return witaTime.toISOString().split("T")[0];
  };

  // Fungsi untuk mendapatkan awal bulan
  const getStartOfMonth = () => {
    const today = new Date();
    const witaOffset = 8 * 60;
    const utc = today.getTime() + today.getTimezoneOffset() * 60000;
    const witaTime = new Date(utc + witaOffset * 60000);
    return new Date(witaTime.getFullYear(), witaTime.getMonth(), 1)
      .toISOString()
      .split("T")[0];
  };

  // Fungsi untuk menggabungkan data performa dari tiga sumber
  const combinePerformanceData = (laporanData, apelData, smartDhData) => {
    const combinedMap = new Map();

    // Helper function untuk mendapatkan working hours dalam menit
    const getWorkingHoursMinutes = (workingHours) => {
      if (!workingHours || workingHours === "0:00") return 0;
      const [hours, minutes] = workingHours.split(":").map(Number);
      return hours * 60 + minutes;
    };

    // Helper function untuk normalisasi skor (0-100)
    const normalizeScore = (value, maxValue) => {
      if (maxValue === 0) return 0;
      return Math.min((value / maxValue) * 100, 100);
    };

    // Process Laporan data
    laporanData.forEach((item) => {
      const uid = item.uid_RSS;
      if (uid) {
        combinedMap.set(uid, {
          uid_RSS: uid,
          nama: item.nama_pegawai,
          foto: `https://smartdh.rotendaokab.go.id/uploads/${uid}.jpg`,
          laporan: {
            jumlah_laporan: item.jumlah_laporan || 0,
            total_kegiatan: item.total_kegiatan || 0,
            persentase_kepatuhan: parseFloat(
              item.persentase_kepatuhan_individual || 0
            ),
          },
          apel: null,
          smartDh: null,
          combinedScore: 0,
        });
      }
    });

    // Process Apel data
    apelData.forEach((item) => {
      const uid = item.uid_RSS;
      if (uid) {
        if (combinedMap.has(uid)) {
          combinedMap.get(uid).apel = {
            pagi_hadir: item.apel_pagi?.hadir || 0,
            pagi_total: item.apel_pagi?.total || 0,
            siang_hadir: item.apel_siang?.hadir || 0,
            siang_total: item.apel_siang?.total || 0,
            persentase_rata_rata: parseFloat(item.persentase_rata_rata || 0),
          };
        } else {
          combinedMap.set(uid, {
            uid_RSS: uid,
            nama: item.nama_pegawai,
            foto: `https://smartdh.rotendaokab.go.id/uploads/${uid}.jpg`,
            laporan: null,
            apel: {
              pagi_hadir: item.apel_pagi?.hadir || 0,
              pagi_total: item.apel_pagi?.total || 0,
              siang_hadir: item.apel_siang?.hadir || 0,
              siang_total: item.apel_siang?.total || 0,
              persentase_rata_rata: parseFloat(item.persentase_rata_rata || 0),
            },
            smartDh: null,
            combinedScore: 0,
          });
        }
      }
    });

    // Process SmartDH data
    smartDhData.forEach((item) => {
      const uid = item.uid;
      if (uid) {
        if (combinedMap.has(uid)) {
          combinedMap.get(uid).smartDh = {
            hadir: item.hadir || 0,
            izin: item.izin || 0,
            sakit: item.sakit || 0,
            workingHours: item.workingHours || "0:00",
            workingHoursMinutes: getWorkingHoursMinutes(item.workingHours),
          };
        } else {
          combinedMap.set(uid, {
            uid_RSS: uid,
            nama: item.nama,
            foto: `https://smartdh.rotendaokab.go.id/uploads/${uid}.jpg`,
            laporan: null,
            apel: null,
            smartDh: {
              hadir: item.hadir || 0,
              izin: item.izin || 0,
              sakit: item.sakit || 0,
              workingHours: item.workingHours || "0:00",
              workingHoursMinutes: getWorkingHoursMinutes(item.workingHours),
            },
            combinedScore: 0,
          });
        }
      }
    });

    // Calculate combined scores (sum of 3 components, then divide by 3)
    const allScores = [];
    combinedMap.forEach((person) => {
      // Each component contributes equally (0-100). Missing data counts as 0.
      const laporanScore = person.laporan
        ? parseFloat(person.laporan.persentase_kepatuhan) || 0
        : 0;
      const apelScore = person.apel
        ? parseFloat(person.apel.persentase_rata_rata) || 0
        : 0;
      const maxWorkingHours = 480; // 8 jam kerja ideal
      const smartDhScore = person.smartDh
        ? normalizeScore(person.smartDh.workingHoursMinutes, maxWorkingHours)
        : 0;

      const sum = laporanScore + apelScore + smartDhScore;
      person.combinedScore = sum / 3;
      allScores.push(person.combinedScore);
    });

    // Sort by combined score and return top 5
    return Array.from(combinedMap.values())
      .sort((a, b) => b.combinedScore - a.combinedScore)
      .slice(0, 5);
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const today = getTodayWITA();
      const startOfMonth = getStartOfMonth();
      // Parallel fetches

      // SmartDH daftar hadir (requires unitKerja: nama_dinas)
      const user = JSON.parse(localStorage.getItem("user"));
      const unitKerja = user?.nama_dinas;
      const smartDhPromise = unitKerja
        ? axios.post(
            "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/ambildhoharianpd",
            { tanggal: today, unitKerja },
            { headers: { "Content-Type": "application/json" } }
          )
        : Promise.resolve({ data: { success: 0, data: [] } });

      // Apel harian per dinas (pagi/siang)
      const apelPromise = axios.get(
        `${API_ESA_OFFICE}apel-harian/dinas/${dinasId}/tanggal/${today}`
      );

      // Laporan harian per dinas (rekap hari ini)
      const laporanPromise = axios.get(
        `${API_ESA_OFFICE}laporan-harian/dinas/${dinasId}/tanggal/${today}`
      );

      // Top performers (rekap periode 1 s/d today)
      const topPerfPromise = axios.get(
        `${API_ESA_OFFICE}laporan-harian/dinas/${dinasId}/laporan?tanggal_mulai=${startOfMonth}&tanggal_akhir=${today}`
      );

      // Apel rekap (periode 1 s/d today)
      const apelRekapPromise = axios.get(
        `${API_ESA_OFFICE}apel-harian/dinas/${dinasId}/rekap?tanggal_mulai=${startOfMonth}&tanggal_akhir=${today}`
      );

      // SmartDH kinerja (periode 1 s/d today)
      const smartDhKinerjaPromise = axios.post(
        "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/instansi/reguler",
        {
          tAwal: startOfMonth,
          tAkhir: today,
          unitKerja,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const [
        smartDhRes,
        apelRes,
        laporanRes,
        topRes,
        apelRekapRes,
        smartDhKinerjaRes,
      ] = await Promise.all([
        smartDhPromise,
        apelPromise,
        laporanPromise,
        topPerfPromise,
        apelRekapPromise,
        smartDhKinerjaPromise,
      ]);

      // Compute SmartDH attendance stats
      const daftarHadir =
        smartDhRes?.data?.success === 1 ? smartDhRes.data.data : [];
      const sdStats = {
        hadir: 0,
        izin: 0,
        sakit: 0,
        cuti: 0,
        tugasBelajar: 0,
        tanpaBerita: 0,
        tidakAdaData: 0,
        total: daftarHadir.length || 0,
      };
      daftarHadir.forEach((item) => {
        switch (item.status) {
          case "Hadir":
            sdStats.hadir++;
            break;
          case "Izin":
            sdStats.izin++;
            break;
          case "Sakit":
            sdStats.sakit++;
            break;
          case "Cuti":
            sdStats.cuti++;
            break;
          case "Tugas Belajar":
            sdStats.tugasBelajar++;
            break;
          case "Tanpa Berita":
            sdStats.tanpaBerita++;
            break;
          default:
            sdStats.tidakAdaData++;
            break;
        }
      });
      setSmartDhStats(sdStats);

      // Compute Apel persentase (pagi/siang) berdasarkan data_pegawai
      const apelData = apelRes?.data || {};
      const dataPegawaiApel = Array.isArray(apelData.data_pegawai)
        ? apelData.data_pegawai
        : [];
      const totalApel = dataPegawaiApel.length || 0;
      const pagiHadir = dataPegawaiApel.filter(
        (p) => p.apel_pagi?.status === "Hadir"
      ).length;
      const siangHadir = dataPegawaiApel.filter(
        (p) => p.apel_siang?.status === "Hadir"
      ).length;

      // Set apel hadir counts
      setApelHadir({
        pagi: pagiHadir,
        siang: siangHadir,
      });

      const pagiPct = totalApel ? Math.round((pagiHadir / totalApel) * 100) : 0;
      const siangPct = totalApel
        ? Math.round((siangHadir / totalApel) * 100)
        : 0;
      setApelStats({ persentasePagi: pagiPct, persentaseSiang: siangPct });

      // Compute Laporan stats
      const lapData = laporanRes?.data || {};
      setLaporanStats({
        totalLaporan:
          lapData?.statistik?.total_laporan || lapData?.total_laporan || 0,
        totalKegiatan:
          lapData?.statistik?.total_kegiatan || lapData?.total_kegiatan || 0,
        totalOutput:
          lapData?.statistik?.total_output || lapData?.total_output || 0,
      });

      // Top performers from rekap
      const dataRekap = topRes?.data || {};
      const top5 = (dataRekap?.data_pegawai || [])
        .slice()
        .sort(
          (a, b) =>
            parseFloat(b.persentase_kepatuhan_individual || 0) -
            parseFloat(a.persentase_kepatuhan_individual || 0)
        )
        .slice(0, 5);
      setTopPerformers(top5);

      // Process Apel Rekap data (top 5 performers)
      const apelRekapData = apelRekapRes?.data?.data_pegawai || [];
      const top5Apel = apelRekapData
        .slice()
        .sort(
          (a, b) =>
            parseFloat(b.persentase_rata_rata || 0) -
            parseFloat(a.persentase_rata_rata || 0)
        )
        .slice(0, 5);
      setApelRekapData(top5Apel);

      // Process SmartDH Kinerja data (top 5 performers)
      const smartDhKinerjaData =
        smartDhKinerjaRes?.data?.success === 1
          ? smartDhKinerjaRes.data.data
          : [];
      const top5SmartDh = smartDhKinerjaData
        .slice()
        .sort((a, b) => {
          const getMinutes = (timeStr) => {
            if (!timeStr || timeStr === "0:00") return 0;
            const [hours, minutes] = timeStr.split(":").map(Number);
            return hours * 60 + minutes;
          };
          return getMinutes(b.workingHours) - getMinutes(a.workingHours);
        })
        .slice(0, 5);
      setSmartDhKinerjaData(top5SmartDh);

      // Combine all three data sources using FULL datasets to enrich each person,
      // then take top 5 after computing integrated score
      const fullLaporanData = dataRekap?.data_pegawai || [];
      const fullApelData = apelRekapRes?.data?.data_pegawai || [];
      const fullSmartDhData = smartDhKinerjaData || [];
      const combinedData = combinePerformanceData(
        fullLaporanData,
        fullApelData,
        fullSmartDhData
      );
      setCombinedTopPerformers(combinedData);

      // Overview/documents fetch removed as not displayed
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dinasId) {
      fetchDashboardData();
    }
  }, [dinasId]);

  // Data untuk charts berdasarkan data real
  const kehadiranData = {
    labels: [
      "Hadir",
      "Izin",
      "Sakit",
      "Cuti",
      "Tugas Belajar",
      "Tanpa Berita",
      "Tidak Ada Data",
    ],
    datasets: {
      label: "Status Kehadiran (SmartDH Hari Ini)",
      data: [
        smartDhStats.hadir,
        smartDhStats.izin,
        smartDhStats.sakit,
        smartDhStats.cuti,
        smartDhStats.tugasBelajar,
        smartDhStats.tanpaBerita,
        smartDhStats.tidakAdaData,
      ],
    },
  };

  const produktivitasData = {
    labels: ["Jumlah Kegiatan", "Jumlah Pegawai Melapor"],
    datasets: {
      label: "Produktivitas (Hari Ini)",
      data: [laporanStats.totalKegiatan, laporanStats.totalLaporan],
    },
  };

  const apelBarData = {
    labels: [
      "Hadir",
      "Izin",
      "Sakit",
      "Cuti",
      "Tugas Belajar",
      "Dinas Luar",
      "Tanpa Berita",
    ],
    datasets: [
      {
        label: "Apel Pagi",
        data: () => {
          // compute from smart stats? we compute from apelStats is insufficient; leave zeros if not set
          return [];
        },
      },
      {
        label: "Apel Siang",
        data: () => {
          return [];
        },
      },
    ],
  };

  // Tabel Top 5 Kinerja Staf (dari rekapLaporanDinas periode bulan berjalan)
  const topColumns = [
    { Header: "No", accessor: "index", width: "6%" },
    {
      Header: "Profil",
      accessor: "profil",
      width: "10%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <img
            src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
            alt={row.original.nama_pegawai}
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #e0e0e0",
            }}
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <MDBox
            display="none"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: "grey.200",
              color: "grey.500",
            }}
          >
            <Icon sx={{ fontSize: 18 }}>person</Icon>
          </MDBox>
        </MDBox>
      ),
    },
    { Header: "Nama", accessor: "nama_pegawai", width: "24%" },
    { Header: "Laporan", accessor: "jumlah_laporan", width: "12%" },
    { Header: "Kegiatan", accessor: "total_kegiatan", width: "12%" },
    {
      Header: "Kepatuhan",
      accessor: "persentase_kepatuhan_individual",
      width: "14%",
      Cell: ({ value }) => (
        <MDBox display="inline-flex" alignItems="center" gap={1}>
          <Icon
            sx={{
              fontSize: 16,
              color:
                parseFloat(value) >= 80
                  ? "#2e7d32"
                  : parseFloat(value) >= 60
                  ? "#ed6c02"
                  : "#d32f2f",
            }}
          >
            {parseFloat(value) >= 80
              ? "workspace_premium"
              : parseFloat(value) >= 60
              ? "military_tech"
              : "flag"}
          </Icon>
          <MDTypography variant="caption" fontWeight="bold">
            {value}%
          </MDTypography>
        </MDBox>
      ),
    },
  ];

  const topRows = (topPerformers || []).map((p, idx) => ({
    index: idx + 1,
    ...p,
  }));

  return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pb={3}>
          {/* Banner Header Slider */}
          <MDBox
            mb={3}
            sx={{
              position: "relative",
              width: "100%",
              height: "200px",
              overflow: "hidden",
              borderRadius: "xl",
            }}
          >
            {coverImages.map((image, index) => (
              <MDBox
                key={index}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  opacity: index === currentImageIndex ? 1 : 0,
                  transition: "opacity 1s ease-in-out",
                }}
              >
                <img
                  src={image}
                  alt={`ESA Office Banner ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </MDBox>
            ))}
          </MDBox>
        <MDBox mb={3} ml={1}>
          <MDTypography variant="h4" fontWeight="bold">
            Dashboard ESA Office
          </MDTypography>
          <MDTypography
            variant="button"
            fontWeight="regular"
            sx={{ fontSize: "16px", color: "#737373" }}
          >
            Monitor keseluruhan aktivitas organisasi - kehadiran, produktivitas,
            dan manajemen dokumen
          </MDTypography>
        </MDBox>
        <MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="info"
                  title="Rekap Kehadiran (SmartDH)"
                  description="Status kehadiran harian pegawai"
                  date={`Data per ${new Date().toLocaleDateString("id-ID")}`}
                  chart={kehadiranData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="success"
                  title="Apel Hadir (Pagi vs Siang)"
                  description={`Pagi: ${apelHadir.pagi} pegawai | Siang: ${apelHadir.siang} pegawai`}
                  date="Data real-time hari ini"
                  chart={{
                    labels: ["Apel Pagi", "Apel Siang"],
                    datasets: {
                      label: "Jumlah Hadir",
                      data: [apelHadir.pagi, apelHadir.siang],
                    },
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="warning"
                  title="Produktivitas (Hari Ini)"
                  description="Laporan harian, kegiatan, dan output"
                  date={`Tanggal ${new Date().toLocaleDateString("id-ID")}`}
                  chart={produktivitasData}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        {/* Top 5 Kinerja Staf - Cards */}
        <MDBox mt={3}>
          <MDTypography variant="h6" color="text" ml={1} mb={1}>
            Top 5 Kinerja Staf (Bulan Berjalan)
          </MDTypography>
          <MDTypography variant="caption" color="text" ml={1}>
            Periode: {new Date(getStartOfMonth()).toLocaleDateString("id-ID")} -{" "}
            {new Date(getTodayWITA()).toLocaleDateString("id-ID")}
          </MDTypography>
          <Grid container spacing={2} mt={0.5}>
            {topPerformers.map((p, idx) => (
              <Grid item xs={12} md={6} lg={4} xl={3} key={p.uid_RSS || idx}>
                <Card>
                  <MDBox p={2} display="flex" alignItems="center" gap={2}>
                    <MDBox position="relative">
                      <img
                        src={`https://smartdh.rotendaokab.go.id/uploads/${p.uid_RSS}.jpg`}
                        alt={p.nama_pegawai}
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "2px solid #e0e0e0",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <MDBox
                        display="none"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          backgroundColor: "grey.200",
                          color: "grey.500",
                        }}
                      >
                        <Icon sx={{ fontSize: 28 }}>person</Icon>
                      </MDBox>
                      <MDBox
                        position="absolute"
                        top={-8}
                        left={-8}
                        sx={{
                          backgroundColor:
                            idx === 0
                              ? "#FFD700"
                              : idx === 1
                              ? "#C0C0C0"
                              : idx === 2
                              ? "#CD7F32"
                              : "#1976d2",
                          color: idx > 2 ? "#fff" : "#000",
                          borderRadius: "50%",
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 12,
                          border: "2px solid #fff",
                        }}
                      >
                        {idx + 1}
                      </MDBox>
                    </MDBox>
                    <MDBox flex={1}>
                      <MDTypography variant="button" fontWeight="bold">
                        {p.nama_pegawai}
                      </MDTypography>
                      <MDBox display="flex" gap={1} mt={0.5} flexWrap="wrap">
                        <MDBox
                          display="inline-flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          <Icon sx={{ fontSize: 16, color: "#1976d2" }}>
                            description
                          </Icon>
                          <MDTypography variant="caption">
                            {p.jumlah_laporan} laporan
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          display="inline-flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          <Icon sx={{ fontSize: 16, color: "#2e7d32" }}>
                            task_alt
                          </Icon>
                          <MDTypography variant="caption">
                            {p.total_kegiatan} kegiatan
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          display="inline-flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          <Icon
                            sx={{
                              fontSize: 16,
                              color:
                                parseFloat(p.persentase_kepatuhan_individual) >=
                                80
                                  ? "#2e7d32"
                                  : parseFloat(
                                      p.persentase_kepatuhan_individual
                                    ) >= 60
                                  ? "#ed6c02"
                                  : "#d32f2f",
                            }}
                          >
                            {parseFloat(p.persentase_kepatuhan_individual) >= 80
                              ? "workspace_premium"
                              : parseFloat(p.persentase_kepatuhan_individual) >=
                                60
                              ? "military_tech"
                              : "flag"}
                          </Icon>
                          <MDTypography variant="caption">
                            {p.persentase_kepatuhan_individual}%
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            ))}
            {topPerformers.length === 0 && !loading && (
              <Grid item xs={12}>
                <MDBox textAlign="center" py={3}>
                  <Icon sx={{ fontSize: 40, color: "text.secondary" }}>
                    military_tech
                  </Icon>
                  <MDTypography variant="button" color="text">
                    Belum ada data kinerja staf untuk periode ini
                  </MDTypography>
                </MDBox>
              </Grid>
            )}
          </Grid>
        </MDBox>

        {/* Top 5 Apel Rekap - Cards */}
        <MDBox mt={3}>
          <MDTypography variant="h6" color="text" ml={1} mb={1}>
            Top 5 Kinerja Apel (Bulan Berjalan)
          </MDTypography>
          <MDTypography variant="caption" color="text" ml={1}>
            Periode: {new Date(getStartOfMonth()).toLocaleDateString("id-ID")} -{" "}
            {new Date(getTodayWITA()).toLocaleDateString("id-ID")}
          </MDTypography>
          <Grid container spacing={2} mt={0.5}>
            {apelRekapData.map((p, idx) => (
              <Grid item xs={12} md={6} lg={4} xl={3} key={p.uid_RSS || idx}>
                <Card>
                  <MDBox p={2} display="flex" alignItems="center" gap={2}>
                    <MDBox position="relative">
                      <img
                        src={`https://smartdh.rotendaokab.go.id/uploads/${p.uid_RSS}.jpg`}
                        alt={p.nama_pegawai}
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "2px solid #e0e0e0",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <MDBox
                        display="none"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          backgroundColor: "grey.200",
                          color: "grey.500",
                        }}
                      >
                        <Icon sx={{ fontSize: 28 }}>person</Icon>
                      </MDBox>
                      <MDBox
                        position="absolute"
                        top={-8}
                        left={-8}
                        sx={{
                          backgroundColor:
                            idx === 0
                              ? "#FFD700"
                              : idx === 1
                              ? "#C0C0C0"
                              : idx === 2
                              ? "#CD7F32"
                              : "#1976d2",
                          color: idx > 2 ? "#fff" : "#000",
                          borderRadius: "50%",
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 12,
                          border: "2px solid #fff",
                        }}
                      >
                        {idx + 1}
                      </MDBox>
                    </MDBox>
                    <MDBox flex={1}>
                      <MDTypography variant="button" fontWeight="bold">
                        {p.nama_pegawai}
                      </MDTypography>
                      <MDBox display="flex" gap={1} mt={0.5} flexWrap="wrap">
                        <MDBox
                          display="inline-flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          <Icon sx={{ fontSize: 16, color: "#1976d2" }}>
                            schedule
                          </Icon>
                          <MDTypography variant="caption">
                            Pagi: {p.apel_pagi?.hadir || 0}/
                            {p.apel_pagi?.total || 0}
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          display="inline-flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          <Icon sx={{ fontSize: 16, color: "#2e7d32" }}>
                            schedule
                          </Icon>
                          <MDTypography variant="caption">
                            Siang: {p.apel_siang?.hadir || 0}/
                            {p.apel_siang?.total || 0}
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          display="inline-flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          <Icon
                            sx={{
                              fontSize: 16,
                              color:
                                parseFloat(p.persentase_rata_rata) >= 80
                                  ? "#2e7d32"
                                  : parseFloat(p.persentase_rata_rata) >= 60
                                  ? "#ed6c02"
                                  : "#d32f2f",
                            }}
                          >
                            {parseFloat(p.persentase_rata_rata) >= 80
                              ? "workspace_premium"
                              : parseFloat(p.persentase_rata_rata) >= 60
                              ? "military_tech"
                              : "flag"}
                          </Icon>
                          <MDTypography variant="caption">
                            {p.persentase_rata_rata}%
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            ))}
            {apelRekapData.length === 0 && !loading && (
              <Grid item xs={12}>
                <MDBox textAlign="center" py={3}>
                  <Icon sx={{ fontSize: 40, color: "text.secondary" }}>
                    schedule
                  </Icon>
                  <MDTypography variant="button" color="text">
                    Belum ada data kinerja apel untuk periode ini
                  </MDTypography>
                </MDBox>
              </Grid>
            )}
          </Grid>
        </MDBox>

        {/* Top 5 SmartDH Kinerja - Cards */}
        <MDBox mt={3}>
          <MDTypography variant="h6" color="text" ml={1} mb={1}>
            Top 5 Kinerja SmartDH (Bulan Berjalan)
          </MDTypography>
          <MDTypography variant="caption" color="text" ml={1}>
            Periode: {new Date(getStartOfMonth()).toLocaleDateString("id-ID")} -{" "}
            {new Date(getTodayWITA()).toLocaleDateString("id-ID")}
          </MDTypography>
          <Grid container spacing={2} mt={0.5}>
            {smartDhKinerjaData.map((p, idx) => (
              <Grid item xs={12} md={6} lg={4} xl={3} key={p.uid || idx}>
                <Card>
                  <MDBox p={2} display="flex" alignItems="center" gap={2}>
                    <MDBox position="relative">
                      <img
                        src={`https://smartdh.rotendaokab.go.id/uploads/${p.uid}.jpg`}
                        alt={p.nama}
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "2px solid #e0e0e0",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <MDBox
                        display="none"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          backgroundColor: "grey.200",
                          color: "grey.500",
                        }}
                      >
                        <Icon sx={{ fontSize: 28 }}>person</Icon>
                      </MDBox>
                      <MDBox
                        position="absolute"
                        top={-8}
                        left={-8}
                        sx={{
                          backgroundColor:
                            idx === 0
                              ? "#FFD700"
                              : idx === 1
                              ? "#C0C0C0"
                              : idx === 2
                              ? "#CD7F32"
                              : "#1976d2",
                          color: idx > 2 ? "#fff" : "#000",
                          borderRadius: "50%",
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 12,
                          border: "2px solid #fff",
                        }}
                      >
                        {idx + 1}
                      </MDBox>
                    </MDBox>
                    <MDBox flex={1}>
                      <MDTypography variant="button" fontWeight="bold">
                        {p.nama}
                      </MDTypography>
                      <MDBox display="flex" gap={1} mt={0.5} flexWrap="wrap">
                        <MDBox
                          display="inline-flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          <Icon sx={{ fontSize: 16, color: "#1976d2" }}>
                            work
                          </Icon>
                          <MDTypography variant="caption">
                            {p.workingHours || "0:00"} jam
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          display="inline-flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          <Icon sx={{ fontSize: 16, color: "#2e7d32" }}>
                            check_circle
                          </Icon>
                          <MDTypography variant="caption">
                            {p.hadir || 0} hadir
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          display="inline-flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          <Icon sx={{ fontSize: 16, color: "#ed6c02" }}>
                            schedule
                          </Icon>
                          <MDTypography variant="caption">
                            {p.izin || 0} izin
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            ))}
            {smartDhKinerjaData.length === 0 && !loading && (
              <Grid item xs={12}>
                <MDBox textAlign="center" py={3}>
                  <Icon sx={{ fontSize: 40, color: "text.secondary" }}>
                    work
                  </Icon>
                  <MDTypography variant="button" color="text">
                    Belum ada data kinerja SmartDH untuk periode ini
                  </MDTypography>
                </MDBox>
              </Grid>
            )}
          </Grid>
        </MDBox>

        {/* Top 5 Kinerja Terintegrasi - Cards dengan Design Berbeda */}
        <MDBox mt={3}>
          <MDTypography variant="h6" color="text" ml={1} mb={1}>
            🏆 Top 5 Kinerja Terintegrasi (Bulan Berjalan)
          </MDTypography>
          <MDTypography variant="caption" color="text" ml={1}>
            Kombinasi performa Laporan Harian, Apel, dan SmartDH
          </MDTypography>
          <Grid container spacing={3} mt={0.5}>
            {combinedTopPerformers.map((p, idx) => (
              <Grid item xs={12} md={6} lg={4} xl={3} key={p.uid_RSS || idx}>
                <Card
                  sx={{
                    background:
                      idx === 0
                        ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
                        : idx === 1
                        ? "linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)"
                        : idx === 2
                        ? "linear-gradient(135deg, #CD7F32 0%, #B8860B 100%)"
                        : "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                    color: "white",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: "100px",
                      height: "100px",
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: "50%",
                      transform: "translate(30px, -30px)",
                    },
                  }}
                >
                  <MDBox p={3} position="relative" zIndex={1}>
                    {/* Ranking Badge */}
                    <MDBox
                      position="absolute"
                      top={16}
                      right={16}
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.2)",
                        borderRadius: "50%",
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 18,
                        border: "2px solid rgba(255,255,255,0.3)",
                      }}
                    >
                      {idx + 1}
                    </MDBox>

                    {/* Profile Section */}
                    <MDBox display="flex" alignItems="center" gap={2} mb={2}>
                      <img
                        src={p.foto}
                        alt={p.nama}
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "3px solid rgba(255,255,255,0.3)",
                          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <MDBox
                        display="none"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "50%",
                          backgroundColor: "rgba(255,255,255,0.2)",
                          color: "white",
                        }}
                      >
                        <Icon sx={{ fontSize: 32 }}>person</Icon>
                      </MDBox>
                      <MDBox flex={1}>
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          color="white"
                          sx={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                        >
                          {p.nama}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="rgba(255,255,255,0.9)"
                          sx={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                        >
                          Skor Terintegrasi: {Math.round(p.combinedScore)}%
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    {/* Performance Metrics */}
                    <MDBox>
                      <MDTypography
                        variant="button"
                        fontWeight="bold"
                        color="white"
                        mb={1}
                        sx={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                      >
                        Performa Detail:
                      </MDTypography>

                      {/* Laporan Performance */}
                      {p.laporan && (
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1}
                          sx={{
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderRadius: 1,
                            px: 1.5,
                            py: 0.5,
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Icon sx={{ fontSize: 16, color: "white" }}>
                              description
                            </Icon>
                            <MDTypography variant="caption" color="white">
                              Laporan: {p.laporan.jumlah_laporan} (
                              {p.laporan.persentase_kepatuhan}%)
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      )}

                      {/* Apel Performance */}
                      {p.apel && (
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1}
                          sx={{
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderRadius: 1,
                            px: 1.5,
                            py: 0.5,
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Icon sx={{ fontSize: 16, color: "white" }}>
                              schedule
                            </Icon>
                            <MDTypography variant="caption" color="white">
                              Apel: {p.apel.persentase_rata_rata}%
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      )}

                      {/* SmartDH Performance */}
                      {p.smartDh && (
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1}
                          sx={{
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderRadius: 1,
                            px: 1.5,
                            py: 0.5,
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Icon sx={{ fontSize: 16, color: "white" }}>
                              work
                            </Icon>
                            <MDTypography variant="caption" color="white">
                              SmartDH: {p.smartDh.workingHours} (
                              {p.smartDh.hadir} hadir)
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      )}

                      {/* Combined Score Bar */}
                      <MDBox mt={2}>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={0.5}
                        >
                          <MDTypography
                            variant="caption"
                            color="rgba(255,255,255,0.9)"
                          >
                            Skor Terintegrasi
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="white"
                            fontWeight="bold"
                          >
                            {Math.round(p.combinedScore)}%
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          sx={{
                            width: "100%",
                            height: 6,
                            backgroundColor: "rgba(255,255,255,0.2)",
                            borderRadius: 3,
                            overflow: "hidden",
                          }}
                        >
                          <MDBox
                            sx={{
                              width: `${Math.min(p.combinedScore, 100)}%`,
                              height: "100%",
                              backgroundColor: "rgba(255,255,255,0.8)",
                              borderRadius: 3,
                              transition: "width 0.3s ease",
                            }}
                          />
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            ))}
            {combinedTopPerformers.length === 0 && !loading && (
              <Grid item xs={12}>
                <MDBox textAlign="center" py={4}>
                  <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
                    emoji_events
                  </Icon>
                  <MDTypography variant="h6" color="text.secondary">
                    Belum ada data kinerja terintegrasi
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    Data akan muncul setelah ada performa dari ketiga sistem
                  </MDTypography>
                </MDBox>
              </Grid>
            )}
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Analytics;
